import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AppHeader,
    Button,
    Collapsible,
    ColumnDataType,
    DataTable,
    Modal,
    MultiSelect,
    Searchbox,
    Spinner,
} from "@tag/tag-components-react-v4";
import { DocList2Filled } from "@tag/tag-icons";
import UsageTelemetryService from "../services/UsageTelemetryService";
import { UsageTelemetryEnvironmentOption, UsageTelemetryStatus } from "../types/UsageTelemetry";
import { ProjectAccents } from "../../General/config/projectAccents";
import styles from "./UsageTelemetry.module.scss";

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(value: unknown): string {
    if (typeof value !== 'string' || value.length === 0) return '';
    const d = new Date(value.replace(' ', 'T'));
    if (isNaN(d.getTime())) return value;
    const day = d.getDate();
    const month = MONTH_NAMES[d.getMonth()];
    const year = String(d.getFullYear()).slice(2);
    return `${day} ${month} ${year}`;
}

function renderDateCell({ getValue }: any) {
    return <>{formatShortDate(getValue())}</>;
}

function renderScoreCell({ getValue }: any) {
    const value = getValue();
    if (value && value !== '+0') {
        return <span className={styles.ScoreCell}>{value}</span>;
    }
    return <>{value}</>;
}

function renderTotalCell({ getValue }: any) {
    const value = getValue();
    const numeric = typeof value === 'number' ? value : Number(value);
    if (numeric && numeric !== 0) {
        return <span className={styles.TotalCell}>{value}</span>;
    }
    return <>{value}</>;
}

const LEGEND_ROWS: { criteria: string; comparison: string; score: number }[] = [
    { criteria: "Days since last audit was completed",                "comparison": "Over 365 Days - Since Last Login", "score": 1000 },
    { criteria: "Days since last action was completed",               "comparison": "Over 90 Days - Since Last Login",  "score": 750 },
    { criteria: "Days since last mock inspection was completed",      "comparison": "Over 60 Days - Since Last Login",  "score": 500 },
    { criteria: "Days since last incident was recorded",              "comparison": "Over 30 Days - Since Last Login",  "score": 250 },
    { criteria: "Days since last incident V3 was recorded",           "comparison": "Over 30 Days - Since Last Login",  "score": 250 },
    { criteria: "Days since last complaint/compliment was submitted", "comparison": "Over 7 Days - Since Last Login",   "score": 100 },
    { criteria: "Days since last audit was completed (7 day bonus)",  "comparison": "Over 7 Days - Since Today",        "score": 50 },
    { criteria: "Days since last action was completed (7 day bonus)", "comparison": "Over 7 Days - Since Today",        "score": 50 },
    { criteria: "Days since last survey was distributed",             "comparison": "Over 90 Days - Since Last Login",  "score": 100 },
    { criteria: "Days since last risk was created",                   "comparison": "Over 90 Days - Since Last Login",  "score": 100 },
    { criteria: "Days since last risk assessment was completed",      "comparison": "Over 90 Days - Since Last Login",  "score": 100 },
];

const COLUMN_CACHE_KEY = 'acc-usage-telemetry-columns';

function UsageTelemetry() {
    const [generating, setGenerating] = useState<boolean>(false);
    const [progressMessage, setProgressMessage] = useState<string | undefined>();
    const [progressPercent, setProgressPercent] = useState<number | undefined>();
    const [taskId, setTaskId] = useState<string | undefined>();
    const [rows, setRows] = useState<any[] | undefined>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [alert, setAlert] = useState<{ type: any; message: string } | undefined>();

    const [environments, setEnvironments] = useState<UsageTelemetryEnvironmentOption[]>([]);
    const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        UsageTelemetryService.getEnvironments()
            .then(envs => {
                if (cancelled) return;
                setEnvironments(envs);
                setSelectedEnvironments(envs.filter(e => e.isDefault).map(e => e.name));
            })
            .catch(() => {
                if (cancelled) return;
                setEnvironments([]);
                setSelectedEnvironments([]);
            });
        return () => { cancelled = true; };
    }, []);

    const filteredRows = useMemo(() => {
        if (!rows) return [];
        const needle = searchTerm.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter(r => (r.tenantName ?? '').toLowerCase().includes(needle));
    }, [rows, searchTerm]);

    const columns = React.useMemo(
        () => [
            { accessorKey: 'tenantName', header: 'Tenant Name' },
            { accessorKey: 'createdDate', header: 'Created', cell: renderDateCell },
            { accessorKey: 'auditCompletedDate', header: 'Audit Completed', cell: renderDateCell },
            { accessorKey: 'actionCompletedDate', header: 'Action Completed', cell: renderDateCell },
            { accessorKey: 'mockInspectionDate', header: 'Mock Inspection', cell: renderDateCell },
            { accessorKey: 'incidentDate', header: 'Incident', cell: renderDateCell },
            { accessorKey: 'incidentV3Date', header: 'Incident V3', cell: renderDateCell },
            { accessorKey: 'lastComplaintCompliment', header: 'Complaint/Compliment', cell: renderDateCell },
            { accessorKey: 'lastSurveyDistributedDate', header: 'Survey Distribution', cell: renderDateCell },
            { accessorKey: 'lastRiskCreatedDate', header: 'Risk Created', cell: renderDateCell },
            { accessorKey: 'lastRiskAssessmentDate', header: 'Risk Assessment', cell: renderDateCell },
            { accessorKey: 'lastLoginDate', header: 'Last Login', cell: renderDateCell },
            { accessorKey: 'employeeCount', header: 'Employees', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'auditScore', header: 'Audit', cell: renderScoreCell },
            { accessorKey: 'actionScore', header: 'Action', cell: renderScoreCell },
            { accessorKey: 'mockInspectionScore', header: 'Mock Insp.', cell: renderScoreCell },
            { accessorKey: 'incidentScore', header: 'Incident', cell: renderScoreCell },
            { accessorKey: 'incidentV3Score', header: 'Incident V3', cell: renderScoreCell },
            { accessorKey: 'complaintComplimentScore', header: 'Cmp/Cmpt', cell: renderScoreCell },
            { accessorKey: 'audit7daysScore', header: 'Audit 7d', cell: renderScoreCell },
            { accessorKey: 'action7daysScore', header: 'Action 7d', cell: renderScoreCell },
            { accessorKey: 'surveyDistributionScore', header: 'Survey', cell: renderScoreCell },
            { accessorKey: 'riskCreatedScore', header: 'Risk Cr.', cell: renderScoreCell },
            { accessorKey: 'riskAssessmentScore', header: 'Risk Ass.', cell: renderScoreCell },
            { accessorKey: 'total', header: 'Total', meta: { dataType: 'number' as ColumnDataType }, cell: renderTotalCell },
        ],
        []
    );

    const canDownload = !!taskId && rows !== undefined && rows.length > 0;

    const handleGenerate = async () => {
        setAlert(undefined);
        setRows(undefined);
        setTaskId(undefined);
        setGenerating(true);
        setProgressMessage('Starting…');
        setProgressPercent(undefined);

        try {
            const { taskId: newTaskId } = await UsageTelemetryService.generate(selectedEnvironments);

            let status: UsageTelemetryStatus;
            do {
                await delay(2000);
                status = await UsageTelemetryService.getStatus(newTaskId);
                setProgressPercent(status.progress ?? undefined);
                setProgressMessage(status.progressMessage ?? 'Working…');
            } while (!status.complete);

            if (status.errorMessage) {
                setAlert({ type: 'error', message: status.errorMessage });
            } else {
                setRows(status.rows ?? []);
                setTaskId(newTaskId);
                setAlert({
                    type: 'success',
                    message: `Report generated — ${status.tenantsQueried} tenants queried, ${status.tenantsFailed} failed.`
                });
            }
        } catch (e) {
            setAlert({
                type: 'error',
                message: 'Failed to generate the usage telemetry report. Please try again or contact HSC Care DevOps.'
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async () => {
        if (!taskId) return;
        try {
            const { blob, filename } = await UsageTelemetryService.downloadReport(taskId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            setAlert({
                type: 'error',
                message: 'Download failed. The report may have expired — regenerate and try again.'
            });
        }
    };

    return (
        <div className={styles.Page}>
            <div className={styles.Header}>
                <h1>Usage Telemetry</h1>
                <h4>Generate a compliance and usage telemetry report across every Care Compliance tenant configured for this environment.</h4>
            </div>

            <AppHeader
                heading="Tenant Scores Report"
                icon={<DocList2Filled />}
                accent={ProjectAccents.CareCompliance}
            >
                <div className={styles.Controls}>
                    <Button
                        text="Generate usage telemetry report"
                        accent={ProjectAccents.CareCompliance}
                        disabled={generating || (environments.length > 0 && selectedEnvironments.length === 0)}
                        onClick={handleGenerate}
                    />
                    {canDownload ? (
                        <Button
                            text="Download report"
                            accent={ProjectAccents.CareCompliance}
                            onClick={handleDownload}
                        />
                    ) : null}
                </div>
            </AppHeader>

            <div className={styles.Legend}>
                <Collapsible title="Scoring legend (11 rules)" defaultExpanded={false}>
                    <div className={styles.LegendInner}>
                        <table className={styles.LegendTable}>
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Criteria</th>
                                    <th>Comparison</th>
                                    <th>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {LEGEND_ROWS.map((rule, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{rule.criteria}</td>
                                        <td>{rule.comparison}</td>
                                        <td>+{rule.score}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Collapsible>
            </div>

            {alert ? (
                <div className={styles.AlertBlock}>
                    <Alert type={alert.type} closeable onClosed={() => setAlert(undefined)}>
                        {alert.message}
                    </Alert>
                </div>
            ) : null}

            <div className={styles.TableBlock}>
                <div className={styles.TableToolbar}>
                    <Searchbox
                        value={searchTerm}
                        onSearch={(value) => setSearchTerm(value)}
                        clearButton
                        placeholder="Search by tenant name"
                        style={{ width: '320px' }}
                    />
                    {environments.length > 0 ? (
                        <MultiSelect
                            data={environments}
                            textField="name"
                            valueField="name"
                            value={selectedEnvironments}
                            onChange={(e: any) => setSelectedEnvironments((e?.value ?? []) as string[])}
                            placeholder="Environments"
                            selectionType="checkbox"
                            style={{ width: '320px' }}
                        />
                    ) : null}
                    {rows !== undefined ? (
                        <span className={styles.RowCount}>
                            {filteredRows.length} of {rows.length} tenants
                        </span>
                    ) : null}
                </div>
                <DataTable
                    isLoading={false}
                    columns={columns}
                    data={filteredRows}
                    columnPicker={{ cacheKey: COLUMN_CACHE_KEY }}
                    accent={ProjectAccents.CareCompliance}
                    height="60vh"
                    noDataElement={
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                            {rows === undefined ? (
                                <>Press <strong>Generate usage telemetry report</strong> to populate this table.</>
                            ) : searchTerm ? (
                                <>No tenants match &ldquo;{searchTerm}&rdquo;.</>
                            ) : (
                                <>No tenants to display.</>
                            )}
                        </div>
                    }
                />
            </div>

            <Modal
                visible={generating}
                accent={ProjectAccents.CareCompliance}
                heading="Generating usage telemetry"
                suppressCloseButton
                primaryButtonProps={{ buttonStyle: { display: 'none' } }}
                cancelButtonProps={{ buttonStyle: { display: 'none' } }}
            >
                <div className={styles.ModalContent}>
                    <Spinner>{progressMessage ?? 'Working…'}</Spinner>
                    {typeof progressPercent === 'number' ? (
                        <span className={styles.ProgressText}>{progressPercent}%</span>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
}

export default UsageTelemetry;
